import { Link } from "react-router-dom";
import { SectionLabel } from "@/components/motion";
import { CONTACT } from "@/lib/data";

const SECTIONS = [
  {
    title: "1. Agreement",
    body: `These Terms of Service govern your use of the website of ELT Freight & Logistics ("ELT", "we", "us"), located at ${CONTACT.address}, ${CONTACT.city}, and any request for services submitted through it. By using this website or submitting a quote request, carrier application or message, you agree to these terms.`,
  },
  {
    title: "2. Nature of Services — Freight Brokerage",
    body: "ELT is an independent freight broker and logistics coordinator. ELT arranges transportation by engaging independent, properly authorized and insured motor carriers. ELT is not a motor carrier, does not take physical possession of freight, and does not transport goods. Transportation itself is performed by the engaged carrier under that carrier's own authorities, insurance and contracts of carriage.",
  },
  {
    title: "3. Canadian & U.S. Regulatory Compliance",
    body: "Cross-border transportation arranged by ELT is performed by carriers holding the required operating authorities for the jurisdictions travelled — including, as applicable, U.S. FMCSA operating authority (MC/DOT), Canadian provincial authorities (e.g., NSC), and customs requirements (ACE/ACI e-Manifest, bonds and related filings). Shippers are responsible for providing accurate commodity, value and documentation information required for customs and regulatory compliance.",
  },
  {
    title: "4. Quotes & Bookings",
    list: [
      "Quotes provided through this website or by email are estimates only and are not binding until confirmed in a written rate confirmation.",
      "A binding agreement for a specific shipment arises only upon ELT's issuance and your acceptance of a rate confirmation for that load.",
      "Rates may change based on weight, dimensions, accessorials, market capacity, fuel, waiting time or inaccuracies in the information provided.",
    ],
  },
  {
    title: "5. Cargo Liability & Insurance",
    body: "As a broker, ELT is not liable for loss, damage or delay to cargo; such claims rest with the transporting carrier, subject to that carrier's liability terms and applicable law (including Canadian provincial carrier liability regimes and U.S. Carmack Amendment principles for interstate shipments). ELT sources carriers that maintain the insurance and operating authority appropriate to the shipment. Optional additional cargo insurance can be arranged on request before booking.",
  },
  {
    title: "6. Shipper Responsibilities",
    list: [
      "Provide complete and accurate shipment details, including weight, dimensions, commodity, hazmat status and handling requirements.",
      "Ensure freight is properly packaged, labelled, loaded and ready at the agreed time.",
      "Comply with all applicable export/import, customs and dangerous goods regulations in Canada and the United States.",
    ],
  },
  {
    title: "7. Carrier Responsibilities",
    body: "Carriers and owner-operators applying to the ELT network must hold valid operating authority, insurance and safety fitness for their stated equipment and lanes, and must keep such credentials current. Engagement for any load is subject to ELT's carrier vetting and a written carrier agreement; nothing on this website creates an employment relationship or guarantees freight volumes.",
  },
  {
    title: "8. Payment",
    body: "Unless otherwise agreed in writing, invoices are payable per the terms stated on the invoice or rate confirmation. Carriers are paid per agreed written terms. Late payments may accrue interest as permitted by applicable law.",
  },
  {
    title: "9. Website Content & Intellectual Property",
    body: "All content on this website — text, design, graphics, logos and the ELT mark — is the property of ELT Freight & Logistics and may not be reproduced without written permission. Website content is provided for general information and does not constitute a rate quote, offer of carriage, or legal or customs advice.",
  },
  {
    title: "10. Limitation of Liability",
    body: "To the maximum extent permitted by law, ELT's aggregate liability arising from this website or its coordination services shall not exceed the fees paid to ELT for the specific shipment giving rise to the claim. ELT is not liable for indirect, incidental, consequential or punitive damages, including lost profits or business interruption.",
  },
  {
    title: "11. Governing Law",
    body: "These terms are governed by the laws of the Province of Alberta and the federal laws of Canada applicable therein. For U.S. domestic or cross-border shipments, U.S. federal transportation law may additionally apply to the underlying carriage. Courts sitting in Calgary, Alberta shall have jurisdiction, except where mandatory law provides otherwise.",
  },
  {
    title: "12. Contact",
    body: `Questions about these terms: ELT Freight & Logistics, ${CONTACT.address}, ${CONTACT.city} — ${CONTACT.dispatch}.`,
  },
];

export default function Terms() {
  return (
    <main className="pt-16" data-testid="terms-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-5 py-20 lg:px-8">
          <SectionLabel>// Legal</SectionLabel>
          <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl">
            Terms of <span className="text-hazard">Service</span>
          </h1>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-dim">Effective: October 2026 — Canada &amp; United States</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <div className="flex flex-col gap-8">
          {SECTIONS.map((s) => (
            <div key={s.title} className="border border-line bg-panel p-7" data-testid={`terms-section-${s.title.split(".")[0]}`}>
              <h2 className="font-display text-xl font-bold uppercase text-bright">{s.title}</h2>
              {s.body && <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">{s.body}</p>}
              {s.list && (
                <ul className="mt-3 flex flex-col gap-2">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-[#9CA3AF]">
                      <span className="mt-2 h-1 w-1 shrink-0 rotate-45 bg-hazard" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <p className="text-sm text-dim">
            See also our <Link to="/privacy" data-testid="terms-privacy-link" className="text-hazard hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </main>
  );
}
